/**
 * Maps POST /user/functions `user_info` payload: snake_case → camelCase.
 * Verified addresses and pending verifications are each A–Z by email, so a
 * later response cannot reshuffle rows. The list renders verified first.
 * @param {Object} [data]
 * @returns {Object} `{ email, additionalEmails, pendingEmailVerifications }`
 */
export function refactorUserInfo(data) {
  const source = data && typeof data === 'object' ? data : {}
  // theme is owned by the settings update

  return {
    email: source.email || null,
    additionalEmails: sortEmails(
      Array.isArray(source.additional_emails) ? source.additional_emails : []
    ),
    pendingEmailVerifications: refactorPendingEmailVerifications(
      source.pending_email_verifications
    ),
  }
}

/**
 * Maps POST /user/functions envelope: `{ status, user_info?, error? }`.
 * @param {Object} [data]
 * @returns {Object} `{ status, userInfo, errorMessage }`
 */
export function refactorUserFunctionsResponse(data) {
  return {
    status: data?.status || '',
    userInfo: data?.user_info ? refactorUserInfo(data.user_info) : null,
    errorMessage: data?.error || '',
  }
}

/** Case-insensitive email equality; empty values never match. */
export function isSameEmail(left, right) {
  const normalizedLeft = normalizeEmail(left)
  return !!normalizedLeft && normalizedLeft === normalizeEmail(right)
}

/** True when `email` is already a verified additional address on `userInfo`. */
export function hasAdditionalEmail(userInfo, email) {
  return (userInfo?.additionalEmails || []).some((item) => isSameEmail(item, email))
}

/** Pending verification entry for `email`, or undefined. */
export function findPendingEmailVerification(userInfo, email) {
  return (userInfo?.pendingEmailVerifications || []).find((entry) =>
    isSameEmail(entry.email, email)
  )
}

/**
 * True when `email` already exists on the account: primary, verified additional,
 * or pending. `sessionEmail` is an optional extra source for callers that compare
 * against a session identity distinct from `userInfo.email`.
 */
export function isEmailOnAccount({ userInfo, email, sessionEmail }) {
  return (
    isSameEmail(email, userInfo?.email) ||
    isSameEmail(email, sessionEmail) ||
    hasAdditionalEmail(userInfo, email) ||
    !!findPendingEmailVerification(userInfo, email)
  )
}

/** True while `user_info` (the primary email source) is not ready to read. */
export function isUserInfoEmailListPending({ isUserInfoLoading, userInfo, userInfoError }) {
  return isUserInfoLoading || (userInfo == null && !userInfoError)
}

/**
 * Distinct account emails for Slack connect: the primary, plus verified additional
 * emails when the company allows them. Empty while `user_info` is pending or failed.
 * @returns {string[]}
 */
export function collectAccountEmailsForSlackConnect({
  userInfo,
  allowAdditionalEmails,
  isEmailListBlocked,
}) {
  if (isEmailListBlocked) return []
  const primary = userInfo?.email || ''
  if (!allowAdditionalEmails) return primary ? [primary] : []

  const collected = []
  for (const email of [primary, ...(userInfo?.additionalEmails || [])]) {
    if (email && !collected.some((existing) => isSameEmail(existing, email))) {
      collected.push(email)
    }
  }
  return collected
}

function normalizeEmail(value) {
  return typeof value === 'string' ? value.trim().toLowerCase() : ''
}

function compareEmails(left, right) {
  return normalizeEmail(left).localeCompare(normalizeEmail(right), 'en', { sensitivity: 'base' })
}

function sortEmails(emails) {
  return [...emails].sort(compareEmails)
}

function refactorPendingEmailVerifications(pendingMap) {
  if (!pendingMap || typeof pendingMap !== 'object' || Array.isArray(pendingMap)) {
    return []
  }

  return Object.entries(pendingMap)
    .map(([email, value]) => {
      const rawExpires = value && typeof value === 'object' ? value.expires_at : ''
      return {
        email,
        expiresAt: parseGmtExpiresAt(rawExpires),
      }
    })
    .sort((left, right) => compareEmails(left.email, right.email))
}

/** Parse GMT `expires_at` safely; invalid values become ''. */
function parseGmtExpiresAt(rawExpires) {
  if (!rawExpires) return ''
  const parsed = new Date(rawExpires)
  return Number.isNaN(parsed.getTime()) ? '' : parsed.toISOString()
}
